import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { GlobalAudioPlayerProvider } from './contexts/GlobalAudioPlayerContext';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import CookieConsent from './components/CookieConsent';
import PageTracker from './components/PageTracker';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import UploadPage from './pages/UploadPage';
import StatisticsPage from './pages/StatisticsPage';
import AdminPage from './pages/AdminPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminCMSPage from './pages/AdminCMSPage';
import AdminSubscribersPage from './pages/AdminSubscribersPage';
import AdminUserDetailPage from './pages/AdminUserDetailPage';
import { ToastProvider } from './contexts/ToastContext';
import ToastContainer from './components/Toast';
import ProfilePage from './pages/ProfilePage';
import FAQPage from './pages/FAQPage';
import PricingPage from './pages/PricingPage';
import DemosPage from './pages/DemosPage';
import FeaturesPage from './pages/FeaturesPage';
import BlogPage from './pages/BlogPage';
import ContactPage from './pages/ContactPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import KVKKPage from './pages/KVKKPage';
import PaymentPage from './pages/PaymentPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import CheckoutPage from './pages/CheckoutPage';
import DevelopmentPage from './pages/DevelopmentPage';

function App() {
  const { isAuthenticated, loadUser, loading, user } = useAuthStore();
  const [loadingTimeout, setLoadingTimeout] = React.useState(false);
  const [trackingEnabled, setTrackingEnabled] = useState(false);

  useEffect(() => {
    // Only load user if token exists and user not already loaded
    const token = localStorage.getItem('access_token');
    if (token && !user && !loading) {
      console.log('App.tsx - Token found, loading user...');
      
      // Set a timeout to prevent infinite loading
      const timeoutId = setTimeout(() => {
        console.warn('App.tsx - Loading timeout, forcing loading to false');
        setLoadingTimeout(true);
      }, 6000); // 6 seconds timeout (slightly longer than loadUser timeout)
      
      loadUser()
        .then(() => {
          console.log('App.tsx - User loaded successfully');
          clearTimeout(timeoutId);
          setLoadingTimeout(false);
        })
        .catch((err: any) => {
          console.error('App.tsx - Failed to load user:', err);
          clearTimeout(timeoutId);
          setLoadingTimeout(false);
          // Token geçersizse temizle
          if (err?.response?.status === 401 || err?.message === 'User loading timeout') {
            localStorage.removeItem('access_token');
          }
        });
      
      return () => clearTimeout(timeoutId);
    } else if (!token) {
      // No token, ensure loading is false immediately
      console.log('App.tsx - No token found, skipping user load');
      setLoadingTimeout(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Check if user is admin
  const isAdmin = React.useMemo(() => {
    const admin = user?.roles?.includes('admin') || false;
    console.log('App.tsx - User:', user?.username, 'IsAdmin:', admin);
    return admin;
  }, [user]);

  // Only show loading on initial mount if we have a token and not timed out
  // Also don't show loading if there's no token (user should see landing page immediately)
  const hasToken = !!localStorage.getItem('access_token');
  const showLoading = hasToken && loading && !user && !loadingTimeout;
  
  if (showLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 dark:border-gray-400 mx-auto"></div>
          <div className="text-main mt-4">Yükleniyor...</div>
        </div>
      </div>
    );
  }

  const handleCookieConsent = (analytics: boolean) => {
    setTrackingEnabled(analytics);
  };

  return (
    <ThemeProvider>
      <LanguageProvider>
        <ToastProvider>
        <Router>
          <GlobalAudioPlayerProvider>
          <PageTracker />
          <CookieConsent onAccept={handleCookieConsent} />
          <ToastContainer />
          <Routes>
            <Route
              path="/"
              element={<LandingPage />}
            />
            <Route
              path="/login"
              element={isAuthenticated ? <Navigate to="/dashboard" /> : <LoginPage />}
            />
            <Route
              path="/register"
              element={isAuthenticated ? <Navigate to="/dashboard" /> : <RegisterPage />}
            />
            <Route
              path="/dashboard"
              element={isAuthenticated ? <DashboardPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/projects/:id"
              element={isAuthenticated ? <ProjectDetailPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/upload"
              element={isAuthenticated ? <UploadPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/statistics"
              element={isAuthenticated ? <StatisticsPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/admin"
              element={
                <AdminProtectedRoute>
                  <AdminDashboard />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <AdminProtectedRoute>
                  <AdminUsersPage />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/users/:username"
              element={
                <AdminProtectedRoute>
                  <AdminUserDetailPage />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/cms"
              element={
                <AdminProtectedRoute>
                  <AdminCMSPage />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/subscribers"
              element={
                <AdminProtectedRoute>
                  <AdminSubscribersPage />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/old"
              element={isAuthenticated ? <AdminPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/profile"
              element={isAuthenticated ? <ProfilePage /> : <Navigate to="/login" />}
            />
            <Route
              path="/faq"
              element={<FAQPage />}
            />
            <Route
              path="/pricing"
              element={<PricingPage />}
            />
            <Route
              path="/demos"
              element={<DemosPage />}
            />
            <Route
              path="/demos/:id"
              element={<ProjectDetailPage demoMode />}
            />
            <Route
              path="/features"
              element={<FeaturesPage />}
            />
            <Route
              path="/blog"
              element={<BlogPage />}
            />
            <Route
              path="/contact"
              element={<ContactPage />}
            />
            <Route
              path="/privacy"
              element={<PrivacyPolicyPage />}
            />
            <Route
              path="/terms"
              element={<TermsOfServicePage />}
            />
            <Route
              path="/kvkk"
              element={<KVKKPage />}
            />
            <Route
              path="/payment"
              element={isAuthenticated ? <PaymentPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/payment/success"
              element={isAuthenticated ? <PaymentSuccessPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/checkout"
              element={<CheckoutPage />}
            />
            <Route
              path="/development"
              element={
                <AdminProtectedRoute>
                  <DevelopmentPage />
                </AdminProtectedRoute>
              }
            />
          </Routes>
          </GlobalAudioPlayerProvider>
        </Router>
        </ToastProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;

